import React, { useMemo } from 'react';
import { StyledOCard } from './index.styles';
import { Button } from '@marriott/mi-ui-library';
import { addSubDirectoryPrefix } from '../../utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import {
  useConditionalPageContextSERP,
  useConversationSearchCriteria,
  useConversationFilterState,
  useConversationLocationState,
} from '../../hooks';
import { buildFindHotelsUrl } from '../../modules/utils/chatAvailabilityUrl';
import { LEISURE_REGION_PIDS_TO_SUPRESS } from '../../constants';
import { AI_SEARCH_TRACK_LABELS } from '../../constants/aiSearchTrackLabels';

interface GeoCodeInfo {
  details?: {
    destinationType?: string;
    distance?: number;
    location?: {
      city?: string;
      address?: string;
      latitude?: number;
      longitude?: number;
      state?: string;
      country?: string;
    };
  };
  placeId?: string;
}

interface OCardProps {
  className?: string;
  onSearchAllClick?: () => void;
  title?: string;
  buttonText?: string;
  geoCodeInfo?: GeoCodeInfo;
}

export const OCard: React.FC<OCardProps> = ({
  className = '',
  onSearchAllClick,
  geoCodeInfo,
  title = 'Looking for more properties?',
  buttonText = 'Search All Properties',
}) => {
  const location = geoCodeInfo?.details?.location;
  const { latitude, longitude, city, state, country, address } = location || {};
  const { placeId } = geoCodeInfo || {};

  // Build location name for display
  const locationName = city || address || '';

  // Build full address string for URL
  const fullAddress = [city, state, country].filter(Boolean).join(', ');

  const envVars = useClientEnvVarsStore(state => state.envVarsObject);
  const { pageVal } = useConditionalPageContextSERP();
  const homeRedirectUrl = envVars['NEXT_PUBLIC_HOME_URL'] || '';
  const findHotelsUrl = envVars['NEXT_PUBLIC_FIND_HOTELS_URL'] || '';

  // Get search criteria from the current conversation's state
  const searchCriteria = useConversationSearchCriteria();

  // Get filter state from the current conversation's state
  const filterState = useConversationFilterState();

  // Get location state from the current conversation's state (for region)
  const locationState = useConversationLocationState();

  // Build the Find Hotels URL with all search criteria and filters
  const findHotelsRedirectURL = useMemo(() => {
    const baseUrl = addSubDirectoryPrefix(findHotelsUrl);

    // Only set region if placeId matches a leisure region placeId
    const currentPlaceId = placeId || locationState.placeId;
    const isLeisureRegionSearch = currentPlaceId && LEISURE_REGION_PIDS_TO_SUPRESS.includes(currentPlaceId);
    const regionValue = isLeisureRegionSearch
      ? country ||
        locationState.country ||
        state ||
        locationState.stateProvince ||
        address ||
        locationState.address ||
        undefined
      : undefined;

    return buildFindHotelsUrl({
      baseUrl,
      placeId: placeId || locationState.placeId || undefined,
      latitude: latitude || locationState.latitude || undefined,
      longitude: longitude || locationState.longitude || undefined,
      locationName: fullAddress || locationName || locationState.destination || undefined,
      city: city || locationState.city || undefined,
      stateProvince: state || locationState.stateProvince || undefined,
      country: country || locationState.country || undefined,
      address: fullAddress || locationState.address || undefined,
      region: regionValue,
      chatSearchCriteria: {
        checkInDate: searchCriteria.checkInDate,
        checkOutDate: searchCriteria.checkOutDate,
        flexibleDate: searchCriteria.flexible,
        numRooms: searchCriteria.numRooms,
        numAdultsPerRoom: searchCriteria.numAdultsPerRoom,
        numChildrenPerRoom: searchCriteria.numChildrenPerRoom,
        childrenAges: searchCriteria.childrenAges,
        useRewardsPoints: searchCriteria.useRewardsPoints,
        clusterCode: searchCriteria.clusterCode,
        corporateCode: searchCriteria.corporateCode,
        groupCode: searchCriteria.groupCode,
      },
      filterState: {
        brands: filterState.brands,
        amenities: filterState.amenities,
        hotelTypes: filterState.hotelTypes,
        activities: filterState.activities,
        transportationTypes: filterState.transportationTypes,
        priceRange: filterState.priceRange,
        pointsRange: filterState.pointsRange,
        distance: filterState.distance,
        events: filterState.events,
        states: filterState.states,
        cities: filterState.cities,
      },
    });
  }, [
    findHotelsUrl,
    placeId,
    latitude,
    longitude,
    locationName,
    fullAddress,
    city,
    state,
    country,
    locationState.placeId,
    locationState.latitude,
    locationState.longitude,
    locationState.destination,
    locationState.city,
    locationState.stateProvince,
    locationState.country,
    locationState.address,
    searchCriteria.checkInDate,
    searchCriteria.checkOutDate,
    searchCriteria.flexible,
    searchCriteria.numRooms,
    searchCriteria.numAdultsPerRoom,
    searchCriteria.numChildrenPerRoom,
    searchCriteria.childrenAges,
    searchCriteria.useRewardsPoints,
    searchCriteria.clusterCode,
    searchCriteria.corporateCode,
    searchCriteria.groupCode,
    filterState.brands,
    filterState.amenities,
    filterState.hotelTypes,
    filterState.activities,
    filterState.transportationTypes,
    filterState.priceRange,
    filterState.pointsRange,
    filterState.distance,
    filterState.events,
    filterState.states,
    filterState.cities,
  ]);

  // Determine the final URL - use findHotelsRedirectURL if we have location info, otherwise fallback to home
  const hasLocationInfo = !!(
    placeId ||
    locationState.placeId ||
    (latitude && longitude) ||
    (locationState.latitude && locationState.longitude) ||
    locationName ||
    locationState.destination
  );
  const finalUrl = hasLocationInfo ? findHotelsRedirectURL : addSubDirectoryPrefix(homeRedirectUrl);

  const handleSearchClick = () => {
    if (onSearchAllClick) {
      onSearchAllClick();
    }
  };

  return (
    <StyledOCard className={`o-card-centered ${className}`} data-testid="o-card">
      <div className="content-wrapper">
        <p className="title-text" data-testid="o-card-title">
          {locationName ? `Looking for all properties in ${locationName}?` : title}
        </p>
      </div>
      <a
        href={finalUrl}
        target={pageVal === 'chat' ? '_blank' : '_self'}
        rel="noreferrer"
        className="search-all-button-link custom_click_track"
        data-custom_click_track_value="AI Search|Exit AI Search|internal|"
        onClick={handleSearchClick}
        {...(pageVal === 'chat' && {
          'data-track-label': AI_SEARCH_TRACK_LABELS.SEARCH_ALL_PROPERTIES_CTA,
        })}
      >
        <Button className="search-all-button m-button-secondary" data-testid="o-card-button">
          {buttonText}
        </Button>
      </a>
    </StyledOCard>
  );
};

export default OCard;
